import api from './api';

/**
 * Channel API Service
 *
 * Handles all API calls related to social media channels:
 * - Fetching connected channels
 * - Initiating OAuth flows
 * - Disconnecting channels
 * - Channel statistics
 */

export interface ChannelConnectedBy {
  id: number;
  fullName: string;
  email: string;
}

export interface Channel {
  id: number;
  platform: string;
  platformUserId: string;
  platformUsername: string | null;
  isActive: boolean;
  metadata: {
    lastTokenError?: string;
    // Canva-specific fields
    canvaTeamId?: string | null;
    canvaUserId?: string;
    displayName?: string;
    label?: string;
    [key: string]: unknown;
  };
  tokenExpiresAt: string | null;
  lastSyncAt: string | null;
  createdAt: string;
  updatedAt: string;
  connectedBy?: ChannelConnectedBy;
}

export interface ChannelStats {
  total: number;
  byPlatform: Record<string, number>;
  expiredTokens: number;
}

export const channelsApi = {
  /**
   * Get all connected channels for current user
   * @param opts.includeInactive - when true, also returns inactive/disconnected channels
   */
  getAll: async (opts?: { includeInactive?: boolean }): Promise<Channel[]> => {
    const params = opts?.includeInactive ? { includeInactive: 'true' } : undefined;
    const response = await api.get('/api/channels', { params });
    return response.data.data;
  },

  /**
   * Get a specific channel by ID
   */
  getById: async (channelId: number): Promise<Channel> => {
    const response = await api.get(`/api/channels/${channelId}`);
    return response.data.data;
  },

  /**
   * Get all channels for a specific platform
   */
  getByPlatform: async (platform: string): Promise<Channel[]> => {
    const response = await api.get(`/api/channels/platform/${platform}`);
    return response.data.data;
  },

  /**
   * Get channel statistics
   */
  getStats: async (): Promise<ChannelStats> => {
    const response = await api.get('/api/channels/stats');
    return response.data.data;
  },

  /**
   * Disconnect a channel (soft delete)
   */
  disconnect: async (channelId: number): Promise<void> => {
    await api.delete(`/api/channels/${channelId}`);
  },

  /**
   * Update channel display name
   */
  updateDisplayName: async (channelId: number, displayName: string): Promise<Channel> => {
    const response = await api.patch(`/api/channels/${channelId}`, { displayName });
    return response.data.data;
  },

  /**
   * Initiate LinkedIn OAuth flow
   * Returns the authorization URL to redirect user to
   */
  connectLinkedIn: async (): Promise<string> => {
    const response = await api.get('/api/auth/linkedin');
    return response.data.data.authUrl;
  },

  /**
   * Manually refresh LinkedIn token (for testing)
   */
  refreshLinkedInToken: async (channelId: number): Promise<void> => {
    await api.post(`/api/auth/linkedin/refresh/${channelId}`);
  },

  /**
   * Initiate Facebook OAuth flow
   * Returns the authorization URL to redirect user to
   * This connects both Facebook Pages and linked Instagram Business accounts
   */
  connectFacebook: async (): Promise<string> => {
    const response = await api.get('/api/auth/facebook');
    return response.data.data.authUrl;
  },

  /**
   * Manually refresh Facebook token (for testing)
   */
  refreshFacebookToken: async (channelId: number): Promise<void> => {
    await api.post(`/api/auth/facebook/refresh/${channelId}`);
  },

  /**
   * Initiate YouTube OAuth flow
   */
  connectYouTube: async (): Promise<string> => {
    const response = await api.get('/api/auth/youtube');
    return response.data.data.authUrl;
  },

  /**
   * Initiate TikTok OAuth flow
   */
  connectTikTok: async (): Promise<string> => {
    const response = await api.get('/api/auth/tiktok');
    return response.data.data.authUrl;
  },

  /**
   * Initiate Pinterest OAuth flow
   */
  connectPinterest: async (): Promise<string> => {
    const response = await api.get('/api/auth/pinterest');
    return response.data.data.authUrl;
  },

  /**
   * Initiate Threads OAuth flow
   */
  connectThreads: async (): Promise<string> => {
    const response = await api.get('/api/auth/threads');
    return response.data.data.authUrl;
  },

  /**
   * Initiate Google My Business OAuth flow
   */
  connectGoogleMyBusiness: async (): Promise<string> => {
    const response = await api.get('/api/auth/google-my-business');
    return response.data.data.authUrl;
  },

  /**
   * Manually refresh Google My Business token
   */
  refreshGoogleMyBusinessToken: async (channelId: number): Promise<void> => {
    await api.post(`/api/auth/google-my-business/refresh/${channelId}`);
  },

  /**
   * Authenticate Bluesky with handle and app password
   * Note: Bluesky uses AT Protocol, not OAuth
   */
  connectBluesky: async (): Promise<string> => {
    // For Bluesky, we'll show a modal to collect credentials
    // This returns a special URL that triggers the modal
    return 'bluesky:auth';
  },

  /**
   * Submit Bluesky credentials
   */
  authenticateBluesky: async (handle: string, appPassword: string): Promise<void> => {
    await api.post('/api/auth/bluesky', { handle, appPassword });
  },

  /**
   * Initiate Canva OAuth flow
   */
  connectCanva: async (): Promise<string> => {
    const response = await api.get('/api/auth/canva');
    return response.data.data.authUrl;
  },

  /**
   * Initiate Google Drive OAuth flow
   *
   * @param teamId - Required when the caller has no resolvable team context
   *   (e.g. agency staff with no home team — CLAUDE.md "Active: User
   *   Migration Testing" / docs/features/gdrive-folder-scoping/SPEC.md §2).
   *   Callers should show a team picker first and pass the chosen team's id;
   *   otherwise the backend 400s with `NO_TEAM_CONTEXT`.
   * @param returnTo - Internal path (e.g. `/blog/new`) to redirect back to
   *   after the Google consent screen, instead of the default
   *   `/settings/channels` (cdb #280 — inline connect from the blog page).
   *   Server-side open-redirect-guarded; omit to keep today's Settings
   *   redirect behavior.
   */
  connectGDrive: async (teamId?: number, returnTo?: string): Promise<string> => {
    const params: { teamId?: number; returnTo?: string } = {};
    if (teamId) params.teamId = teamId;
    if (returnTo) params.returnTo = returnTo;
    const response = await api.get('/api/auth/gdrive', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data.data.authUrl;
  },
};

export default channelsApi;
